/**
 * promptStore — editable AI prompt text blocks with localStorage persistence.
 *
 * All static text injections fed to the AI models live here as named blocks.
 * Edit them from the Prompt Library UI; changes persist across sessions.
 */

const STORAGE_KEY = "creative_prompt_library_v1";

// ─── Defaults ────────────────────────────────────────────────────────────────

export const PROMPT_DEFAULTS = {

  // ── Scene mode: concept generation copy (VariantsControls SCENE_MODE_COPY) ──

  scene_mode_ad: `You are my creative strategist for ManMade Labs — premium men's grooming. Build static ad concepts.

Keep it simple:
- Emotional headline that's easy to read, easy to visualize, and hard to object to.
- The static must be instantly understandable with zero prior context.
- Create a solid open loop — leave one thing unresolved so the viewer has to keep looking.
- Make the viewer do a little work to get it. Don't spell everything out — when they figure it out themselves, that's the micro-commitment.
- Reduce the elements. Few words, few objects, breathing room.
- Pattern-interrupt illustration style — modern, scroll-stopping. If a reference image is attached, match its style. Apply the product/table inputs provided.
- This runs on a phone: text comfortably readable at a glance — never tiny, never blown up in the viewer's face.
`,

  scene_mode_product: `Acceptable generation formats (vary across outputs):
- VS comparison chart: razor/competitor vs ManMade with x/check benefit rows.
- Before/after split: skin results, razor bumps cleared, "How It Started / How It's Going."
- Guarantee hero: "30-Day Guarantee" or "You Love It Or We'll Buy It Back" as dominant text with product as the secondary anchor.
- Bundle stack: trimmer + aftershave + guards + travel bag + brush, each with crossed-out price to FREE.
- Mechanism reveal: macro close-up of safety guards, dual heads, ceramic blade, labeled with feature callouts.
- Stat/proof chart: "98% less cuts," "4,600+ five-star reviews," "Zero exposed blades" rendered as bold data graphics with product.
- Bathroom-in-context: product staged on vanity, shower shelf, or counter with no person visible, used as a quiet social-proof signal.
- Text-dominant hero: bold headline carries the ad, product supports.
- Wife's text message overlay: screenshot-style text bubble plus product as visual anchor.
- Component isolation: single-part hero (blade head, guard, aftershave bottle) in extreme macro.`,

  scene_mode_review: `Authentic customer review photos. iPhone-quality shots on bathroom counters, sinks with water drops, messy real-life surfaces. Imperfect lighting, lived-in spaces — not staged. 1 image only. no grid. make it modern bathrooms and countertops. the background shouldnt be a complete mess but should show signs of use.show the scrubber on countertops, sinks, etc. `,

  scene_mode_custom: `Custom scene mode. The user's special instructions define the exact visual scene, composition, and style. Treat the special instructions as the highest-priority creative brief — they override default scene constraints. Still apply product identity, brand guidelines, tags, and all knowledge folder context as supporting material. Vary concepts across the requested quantity while staying true to the custom scene direction.`,

  // ── Scene rules: variant generation (non-image intent) ────────────────────

  scene_rule_ad_variant: `- Scene mode: product-forward static ad.
- Match the product exactly from reference images.
- Use the ManMade trimmer, accessory, or bundle as the clear primary subject.
- Backgrounds can vary across studio, bathroom, moody-surface, or editorial-negative-space setups, but should never feel corporate sterile or stock-photo generic.
- Keep lifestyle context light and the message instantly legible in 1 second.
- Vary across these formats: VS comparison chart, before/after split, guarantee hero, bundle stack, mechanism reveal, stat/proof chart, bathroom-in-context, text-dominant hero, wife's text message overlay, component isolation.`,

  scene_rule_product_variant: `- Scene mode: product photography.
- CRITICAL: The product MUST exactly match the reference images from the knowledge folder. Match the precise shape, colors, materials, textures, label, and packaging. Do NOT invent a different-looking product.
- Generate product images optimized for PDP pages, e-commerce, and conversion.
- Vary angles, compositions, and contexts: hero shots, detail macros, lifestyle use-case, before/after, feature callouts, scale shots, flat-lays, unboxing.
- Clean, professional lighting. Product is always the clear hero.
- For headlines: draw from ALL knowledge sources — copy library, brand messaging, product notes, analysis knowledge. Headlines should reflect the full knowledge base, not just generic product claims.`,

  scene_rule_review_variant: `- Scene mode: customer review photo.
- Generate images that look like real customers took them on their phone.
- Imperfect iPhone quality, messy real-life surfaces (bathroom counter with water drops, sink edge, nightstand).
- Lived-in spaces, not staged. Mixed lighting, casual product placement, everyday clutter.`,

  scene_rule_custom_variant: `- Scene mode: custom. The user's special instructions define the scene and visual direction — treat them as the primary creative brief.
- Apply product identity, brand guidelines, tags, and knowledge folder context as supporting constraints.
- Vary concepts across variants while honoring the custom scene direction throughout.`,

  // ── Scene rules: image generation (image intent) ─────────────────────────

  scene_rule_ad_image: `- Scene mode: product-forward static ad. Organic, real, grounded — NOT animated, NOT cinematic, NOT action-movie style. This should look like a real photo-based ad, not a render.
- NO people, NO faces, NO models. Ignore any source references that show people — translate the concept into a product-only execution.
- Match the product EXACTLY from reference images. Same shape, silhouette, proportions, colors, materials, textures, and logo. Do not redesign it.
- The product sits on a real surface in a real scene. No floating objects, no explosive motion graphics, no dramatic splash/water effects, no surreal environments.
- Backgrounds: real textured surfaces — bathroom counter, marble, concrete, dark wood, linen, wet stone. Grounded, not abstract.
- Style: real photography feel. Natural or soft studio lighting. NOT hyper-stylized, NOT neon-lit, NOT CGI.
- Gold (#D4A85A): use ONLY on trimmer hardware and 1-2 headline accent words. Do not spread gold elsewhere.
- Typography: heavy black sans-serif, two font styles max, mobile-readable. Text is real and legible — no stylized glow, no motion blur on type.
- Every creative does ONE job, obvious in 1 second.
- Vary across: VS comparison chart; guarantee hero; bundle stack; mechanism reveal; stat/proof chart; bathroom-in-context (product only); text-dominant hero; component isolation.`,

  scene_rule_product_image: `Product detail page (PDP) infographic carousel. Each slide is a standalone info graphic with the product as the visual anchor and bold text communicating ONE piece of information. No lifestyle photography. No people. No faces. No hands. No shower scenes. Every slide must have text on it.

Product accuracy is critical: The product must match the reference images exactly — same shape, silhouette, proportions, colors, materials, textures, logo.

CRITICAL — RANDOMIZE SLIDE TYPE: Before generating, pick ONE slide type at random from the list below. Do NOT default to the first option. Use a different type than any previously generated image in this set. Every generation must produce a visually distinct slide type.

Slide types (choose ONE at random):
• VS comparison chart — product vs competitor with ❌/✓ benefit rows
• Feature callouts — product with labeled arrows pointing to key features
• Before/after split — skin results with "How It Started / How It's Going" labels
• Stat/proof slide — bold number ("4,600+ reviews," "98% less cuts") with product as anchor
• Guarantee hero — "30-Day Guarantee" or "Love It or We Buy It Back" as dominant text
• Bundle/what's included — product lineup with each component labeled and priced (crossed-out → FREE)
• Macro detail + callout — extreme close-up with a single text label
• Testimonial overlay — customer quote or text-message screenshot with product as anchor
• Text-dominant hero — bold headline fills the slide, product supports
• Hero + headline — product centered with bold claim text

Design: Clean off-white, warm stone-textured, or dark moody backgrounds. Heavy black sans-serif typography. Gold (#D4A85A) only on product hardware and 1–2 accent words. Mobile-readable. Editorial graphic design, not CGI, not lifestyle.`,

  scene_rule_review_image: `- Scene mode: customer review photo. Real iPhone shots — mostly product sitting somewhere, occasionally being used.
- Camera quality: slightly noisy, not perfectly sharp, phone camera depth of field, auto white balance (sometimes warm/cool cast).
- DISTRIBUTION: 8 out of 10 shots show the product sitting on a surface with NO person visible. Only 2 out of 10 show the product in use — and even then NO face, tight crop on hand and product only.

SURFACE SHOTS (8/10) — product sitting, no person:
  1. Bathroom counter — product on a wet counter with water drops, soap residue, maybe a bar of soap nearby. Shot from above or slight angle.
  2. Sink edge — product balanced on the rim of a sink, faucet visible in background, water on the porcelain.
  3. Shower rack/shelf — product on a shower caddy or shelf, wet tiles behind it, steam or condensation on the wall.
  4. Unboxed on counter — product just removed from its box, packaging sitting next to it on a bathroom counter.
  5. Nightstand or shelf — product sitting casually on a nightstand or bathroom shelf with other everyday items.

IN-USE SHOTS (2/10 only) — tight crop, NO face:
  6. Hand holding product — one hand gripping the scrubber, skin slightly wet, back camera angle. Cut off at the wrist or shoulder. Absolutely no face.
  7. Product against skin — scrubber pressed against an arm, neck, or chest showing lather. Tight macro crop. No face in frame.

- Environment: real lived-in bathroom. Slightly messy, not gross. Mixed lighting, fluorescent or warm overhead.
- NO faces, NO selfies, NO mirror shots, NO posed ad setups, NO studio lighting.
- YES: water drops, crooked angle, harsh overhead light, product partially obscured by other bathroom items.`,

  scene_rule_custom_image: `- Scene mode: custom. The special instructions above define the exact scene — follow them as the primary visual brief.
- Apply all product identity rules: match the product exactly from reference images (same shape, colors, materials, textures, logo). Do not redesign or substitute the product.
- Apply brand rules: ManMade masculine aesthetic, gold (#D4A85A) only on hardware and 1-2 accent words, no corporate sterile backgrounds.
- Let the special instructions override any default scene constraints around surfaces, lighting, or composition.
- Still vary concepts meaningfully across the requested quantity while staying true to the custom scene direction.`,

  // ── Native ad system prompt ───────────────────────────────────────────────

  native_ad_system: `
========================
STATIC AD RULES
========================

These ads are product-forward ManMade static ads. The trimmer, accessory, or bundle is the primary subject.

PRODUCT FIDELITY IS #1 PRIORITY:
- The product in the image must EXACTLY match the knowledge folder reference images. Same shape, same colors, same label, same packaging. Do not redesign or reimagine the product.
- The product can live in-studio on a textured surface, in a real bathroom context (vanity, shower shelf, counter, marble, tile), on dark moody surfaces, or in clean editorial negative space.
- Backgrounds should vary across variants, but must never feel corporate sterile or stock-photo generic.
- Light lifestyle context is welcome (bathroom, shower, nightstand). Heavy lifestyle (models, faces, full bodies) is not.

GOLD SINGULARITY PRINCIPLE:
- Gold (#D4A85A) appears ONLY on trimmer hardware (power buttons, battery dots) and 1-2 accent words in the headline.
- Do not use gold as a general background, glow, border, or accent system anywhere else.

TYPOGRAPHY:
- Heavy black sans-serif.
- One clear hierarchy.
- Mobile-readable.
- Every creative does ONE job visible in 1 second.

ACCEPTABLE GENERATION FORMATS - vary across variants:
- VS comparison chart: razor/competitor vs ManMade with x/check benefit rows.
- Before/after split: skin results, razor bumps cleared, "How It Started / How It's Going."
- Guarantee hero: "30-Day Guarantee" or "You Love It Or We'll Buy It Back" as dominant text with product as the secondary anchor.
- Bundle stack: trimmer + aftershave + guards + travel bag + brush, each with crossed-out price to FREE.
- Mechanism reveal: macro close-up of safety guards, dual heads, ceramic blade, labeled with feature callouts.
- Stat/proof chart: "98% less cuts," "4,600+ five-star reviews," "Zero exposed blades" rendered as bold data graphics with product.
- Bathroom-in-context: product staged on vanity, shower shelf, or counter with no person visible, used as a quiet social-proof signal.
- Text-dominant hero: bold headline carries the ad, product supports.
- Wife's text message overlay: screenshot-style text bubble plus product as visual anchor.
- Component isolation: single part hero (blade head, guard, aftershave bottle) in extreme macro.

Keep the concept simple, performance-first, and instantly understandable.`,

  // ── Ad copy: role prompts ─────────────────────────────────────────────────

  ad_copy_role_ads: `You generate primary text and headlines for static image ad creatives that do NOT look like ads. Your output must never read like marketing. People scroll past anything that smells like a brand trying to sell them something. Your job is to produce copy that feels native to the feed.`,

  ad_copy_role_organic: `You are writing short-form social media copy for a consumer brand.`,

  // ── Ad copy: meta ads format rules ───────────────────────────────────────

  ad_copy_format_meta: `Return exactly 5 headlines and 5 primary texts. Never return fewer than 5 in either list.

## ANALYSIS GATE (run internally before writing copy)
Before writing, internally determine these 5 points from the analysis JSON. Do NOT include this in output — use it to guide your copy:
1. MARKET SOPHISTICATION (1-5, Breakthrough Advertising): 1=raw claim works, 2=need bigger claim, 3=need MECHANISM, 4=need better mechanism, 5=sell identity/transformation
2. AUDIENCE AWARENESS (1-5, Schwartz): 1=unaware, 2=problem-aware, 3=solution-aware, 4=product-aware, 5=most aware
3. CORE DESIRE: What the customer actually wants as a RESULT (not the product — the outcome in their vocabulary)
4. WHY THIS AD STOPS THEM: The scroll-stop mechanism (recognition, tension, screenshot, meme?)
5. THE SINGLE IDEA: One thought this creative delivers. All copy must serve this one thought.

Match copy style to awareness:
- Low (1-2): lead with problem or pain. Don't name the product too early.
- Mid (3): lead with what makes this different in plain words.
- High (4-5): lead with offer, proof, or risk reversal.

## READING LEVEL
- Every line must read at or below US 5th-grade level.
- Use short, common words.
- Avoid jargon, corporate phrasing, and long sentence chains.

## PRIMARY TEXT RULES (1-2 short sentences, 8-30 words total)
Pick one pattern:
Pattern A (Pain → Fix → Soft CTA):
  1. Open with a simple real-life pain point
  2. Say how the product fixes it in plain words
  3. End with a soft next step if needed

Pattern B (Recognition → Relief):
  1. Start with a "yep, that's me" moment
  2. Reveal the fix simply
  3. Land on relief, ease, or confidence

Pattern C (Mini Story):
  1. One short first-person moment
  2. The turning point
  3. Product enters as the easy answer

ALL primary texts MUST contain:
1. One clear concern, outcome, proof point, or use case
2. Simple words a 10-11 year old could read
3. Final ad copy only, ready to post
4. No labels, bullets, notes, or analysis

Voice: Use the customer's own vocabulary. Sound like a person, not a brand.

## HEADLINE RULES (2-6 words)
- Specific, not clever. Simple wins.
- Mechanism OR outcome — pick one.
- Match awareness level: low → lean outcome, high → lean offer.
- Use plain spoken words only.
Bad: "Boost Your Love" (vague slogan), "The Best Trimmer For Men" (generic), "Shop Now — Limited Time" (pure ad language)
Good: "No More Razor Nicks", "Built For Sensitive Skin", "Trim Without The Fear"

## BANNED ELEMENTS (never include in copy)
- Slogan-only taglines with nothing underneath
- Generic "experts recommend" / "scientifically proven" without a specific mechanism or number
- "Shop now!" / "Click the link!" / "Limited time!" as headlines
- Hype punctuation or emoji spam
- Copy so generic it could apply to any product in the category
- Descriptions of the image, layout, visual composition, camera, or "this ad"
- Labels like "Headline:", "Primary Text:", "CTA:"

## VARIETY (across the 5 outputs)
- Mix at least 3 different emotional triggers (fear, embarrassment, partner approval, identity, recognition, humor, relief)
- Vary primary text patterns (A, B, C)
- All 5 headlines must be different
- All 5 primary texts must be different
- One should be noticeably stranger or more conceptual than the rest`,

  ad_copy_format_organic: `Write:
1) A short video description (1-2 sentences)
2) 3 hashtag bundles (4-6 hashtags each)

Description rules:
- Start with a relatable moment, need state, payoff, or curiosity hook
- Make the product or offer feel useful and easy to understand
- Sound like a real person, not a corporate brief

Hashtag rules:
- Mix broader discovery hashtags with niche product/use-case hashtags
- Keep hashtags relevant to the product, use case, or audience
- Avoid generic filler tags like #Cool or #ForYou
- Avoid spammy hashtags`,
};

// ─── Metadata (labels + categories shown in the editor) ──────────────────────

export const PROMPT_META = {
  scene_mode_ad:            { label: "Ad — Scene Brief",          category: "Scene Modes" },
  scene_mode_product:       { label: "Product — Scene Brief",     category: "Scene Modes" },
  scene_mode_review:        { label: "Review — Scene Brief",      category: "Scene Modes" },
  scene_mode_custom:        { label: "Custom — Scene Brief",      category: "Scene Modes" },

  scene_rule_ad_variant:    { label: "Ad — Variant Rule",         category: "Scene Rules: Variants" },
  scene_rule_product_variant:{ label: "Product — Variant Rule",   category: "Scene Rules: Variants" },
  scene_rule_review_variant:{ label: "Review — Variant Rule",     category: "Scene Rules: Variants" },
  scene_rule_custom_variant:{ label: "Custom — Variant Rule",     category: "Scene Rules: Variants" },

  scene_rule_ad_image:      { label: "Ad — Image Rule",           category: "Scene Rules: Images" },
  scene_rule_product_image: { label: "Product — Image Rule",      category: "Scene Rules: Images" },
  scene_rule_review_image:  { label: "Review — Image Rule",       category: "Scene Rules: Images" },
  scene_rule_custom_image:  { label: "Custom — Image Rule",       category: "Scene Rules: Images" },

  native_ad_system:         { label: "Native Ad System Prompt",   category: "Native Ad" },

  ad_copy_role_ads:         { label: "Role Prompt — Ads",         category: "Ad Copy" },
  ad_copy_role_organic:     { label: "Role Prompt — Organic",     category: "Ad Copy" },
  ad_copy_format_meta:      { label: "Format Rules — Meta Ads",   category: "Ad Copy" },
  ad_copy_format_organic:   { label: "Format Rules — Organic",    category: "Ad Copy" },
};

export const PROMPT_CATEGORIES = [
  "Scene Modes",
  "Scene Rules: Variants",
  "Scene Rules: Images",
  "Native Ad",
  "Ad Copy",
];

// ─── In-memory store (primary) + localStorage fallback ───────────────────────
// App.jsx calls initPromptLibrary(data) after loading user_settings from Supabase.
// Any write calls _onChange so App.jsx can persist to Supabase via setUserSettings.

let _mem = {}; // authoritative in-memory overrides
let _onChange = null; // callback → (overrides: Record<string,string>) => void

// Called by App.jsx once user_settings loads
export function initPromptLibrary(data = {}) {
  _mem = { ...(data || {}) };
  // Also mirror to localStorage as offline fallback
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_mem)); } catch {}
}

// Called by App.jsx to subscribe — fires on every change with the new overrides blob
export function onPromptLibraryChange(fn) {
  _onChange = fn;
}

function _flush() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_mem)); } catch {}
  _onChange?.({ ..._mem });
}

function _localFallback() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}

export function getPromptBlock(key) {
  const overrides = Object.keys(_mem).length > 0 ? _mem : _localFallback();
  return overrides[key] !== undefined ? overrides[key] : (PROMPT_DEFAULTS[key] ?? "");
}

export function setPromptBlock(key, value) {
  if (value === PROMPT_DEFAULTS[key] || value === "") {
    delete _mem[key];
  } else {
    _mem[key] = value;
  }
  _flush();
}

export function resetPromptBlock(key) {
  delete _mem[key];
  _flush();
}

export function resetAllPromptBlocks() {
  _mem = {};
  _flush();
}

export function getOverriddenKeys() {
  const overrides = Object.keys(_mem).length > 0 ? _mem : _localFallback();
  return Object.keys(overrides);
}

export function isPromptBlockModified(key) {
  const overrides = Object.keys(_mem).length > 0 ? _mem : _localFallback();
  return overrides[key] !== undefined;
}

export function getAllOverrides() {
  return { ..._mem };
}
